import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS, NotificationService } from 'ui-components/services/notification-service';

import style from '../../../../../components/grid-header/reusable-grid-button.module.less';
import { STOCK_AUDIT_CREATED } from '../../../../../constants/analytics-events';
import { useLuxonUserDateFormat } from '../../../../../hooks/use-datetime-formats';
import { useModal } from '../../../../../hooks/use-modal';
import { useSendMetrics } from '../../../../../hooks/use-send-metrics';
import Global from '../../../../../store/global-selectors';
import { useInventoryContext } from '../../inventory-context';
import { AuditFormModal } from '../../stock-audit/components/audit-form-modal/audit-form-modal';
import { createStockAudit } from '../../stock-audit/stock-audits-api';
import { messages } from './messages';

type InventoryEntity = {
  productId: string | number;
  inventoryLocationId: string | number;
  inventoryWarehouseId: string;
  sku?: string;
  productName?: string;
  stock?: number;
};

const createErrorContentByCode: Record<string, { defaultMessage: string; description: string }> = {
  NotFound: messages.assignAuditCreateErrorNotFoundContent,
  StockAuditActiveAuditConflict: messages.assignAuditCreateErrorActiveConflictContent,
};

const getCreateErrorContent = (error: unknown) => {
  const errors = (error as { response?: { data?: { errors?: { code?: string }[] } } })?.response?.data?.errors;
  const matchedMessage = errors?.map(({ code }) => code && createErrorContentByCode[code]).find(Boolean);
  return matchedMessage ?? messages.assignAuditCreateErrorGenericContent;
};

export const AssignAuditButton = () => {
  const intl = useIntl();
  const { inventoryWarehouseId } = useParams();
  const { selectedProductIds, refreshGridData } = useInventoryContext();
  const { isOpen: isModalOpen, open: openModal, close: closeModal } = useModal();
  const luxonDateFormat = useLuxonUserDateFormat();
  const trackMetrics = useSendMetrics();
  const [isSaving, setIsSaving] = useState(false);

  const selectedInventoryEntities = useSelector((state) =>
    Global.selectEntitiesInventoryByProductIds(state, { productIds: selectedProductIds }),
  ) as InventoryEntity[];

  const auditProducts = useMemo(() => {
    const seen = new Set<string>();
    return selectedInventoryEntities.reduce<{ productId: string; sku: string; productName: string }[]>((acc, entity) => {
      const key = String(entity.productId);
      if (!seen.has(key)) {
        seen.add(key);
        acc.push({ productId: key, sku: entity.sku ?? '', productName: entity.productName ?? '' });
      }
      return acc;
    }, []);
  }, [selectedInventoryEntities]);

  const distinctProductCount = new Set(selectedInventoryEntities.map(({ productId }) => productId)).size;

  const hasWarehouse = !!inventoryWarehouseId;
  const hasSelection = selectedProductIds.length > 0;
  const isDisabled = !hasWarehouse || !hasSelection;

  let disabledTooltipMessage;
  if (!hasWarehouse) {
    disabledTooltipMessage = intl.formatMessage(messages.assignAuditButtonRootViewTooltip);
  } else if (!hasSelection) {
    disabledTooltipMessage = intl.formatMessage(messages.assignAuditButtonNoProductsTooltip);
  }

  const handleConfirm = useCallback(
    async ({
      auditName,
      dueDate,
      assigneeUserId,
      notes,
    }: {
      auditName: string;
      dueDate: string;
      assigneeUserId: string;
      notes: string;
    }) => {
      if (!inventoryWarehouseId) {
        return;
      }
      const products = selectedInventoryEntities
        .filter((entity) => entity.inventoryWarehouseId === inventoryWarehouseId)
        .map(({ productId, inventoryLocationId }) => ({ productId, inventoryLocationId }));

      if (products.length === 0) {
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.assignAuditCreateError} />,
          content: <FormattedMessage {...messages.assignAuditCreateErrorNoProductsContent} />,
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
        return;
      }

      setIsSaving(true);
      try {
        await createStockAudit({ auditName, dueDate, assigneeUserId, notes, inventoryWarehouseId, products }, luxonDateFormat);
        trackMetrics(STOCK_AUDIT_CREATED, {
          productCount: new Set(products.map(({ productId }) => productId)).size,
        });
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.assignAuditCreateSuccess} />,
          notificationType: NOTIFICATION_TYPE_SUCCESS,
        });
        refreshGridData();
        closeModal();
      } catch (error) {
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.assignAuditCreateError} />,
          content: <FormattedMessage {...getCreateErrorContent(error)} />,
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [closeModal, inventoryWarehouseId, luxonDateFormat, refreshGridData, selectedInventoryEntities, trackMetrics],
  );

  return (
    <>
      <Button
        className={style['reusable-button']}
        disabled={isDisabled}
        disabledTooltipMessage={disabledTooltipMessage}
        onClick={openModal}
        theme={THEME_CONSTANTS.tertiary}
      >
        <FormattedMessage {...messages.assignAuditButton} values={{ count: distinctProductCount }} />
      </Button>
      {isModalOpen ? (
        <AuditFormModal
          captionMessage={messages.assignAuditModalSummary}
          captionValues={{ count: distinctProductCount }}
          confirmLabel={messages.assignAuditModalConfirmButton}
          isSaving={isSaving}
          onClose={closeModal}
          onConfirm={handleConfirm}
          products={auditProducts}
          titleMessage={messages.assignAuditModalTitle}
        />
      ) : null}
    </>
  );
};
